import { ref } from "vue";
import router, { route, ROUTE_NAME } from "@/router";
import axios, { AxiosHeaders, AxiosInstance } from "axios";
import { useAuth } from "@/api-connectors/auth";

import { useUserStore } from "@/stores/user";
import { usePersistentStore } from "@/stores/persistent";

import { getPreference } from "@/functions/preferences";
import { Device } from "@capacitor/device";
import { APP_ENVIROMENT } from "@/types/enum/general";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

const testCount = ref<number>(1)
const initDone = ref<boolean>(false)

const api: AxiosInstance = axios.create()

export function useApi() {
    const changeURL = (url: string) => {
        const persistentStore = usePersistentStore()
        persistentStore.apiUrl = url
        api.defaults.baseURL = url
    }

    const setClientInformationHeaders = async () => {
        const { operatingSystem, platform, webViewVersion, osVersion } = await Device.getInfo()
        let binaryVersion: string | undefined = undefined
        if (Capacitor.isNativePlatform()) {
            const { version } = await App.getInfo()
            binaryVersion = version
        }

        api.defaults.headers.common['X-Platform'] = platform === 'web' ? 'web' : 'mobile'
        api.defaults.headers.common['X-App-Version'] = process.env.VUE_APP_VERSION_NAME ?? 'No Version found'
        api.defaults.headers.common['X-Client-Version'] = btoa(JSON.stringify({
            platform: platform === 'web' ? APP_ENVIROMENT.WEB : APP_ENVIROMENT.APP,
            version: process.env.VUE_APP_VERSION_NAME ?? 'No Version found',
            binaryVersion,
            webViewVersion,
            os: operatingSystem,
            osVersion
        }))
    }

    const initApi = async () => {
        if (initDone.value) return

        let persistentPref = undefined

        try {
            const persistentString = await getPreference('persistentStore')
            if (persistentString) persistentPref = JSON.parse(persistentString)
        } catch (e) {
            console.error(e)
        }

        if (persistentPref?.apiUrl) {
            api.defaults.baseURL = persistentPref?.apiUrl
        } else {
            if(typeof ENV === 'object' && ENV.API_URL) changeURL(ENV.API_URL)
            else if (process.env?.VUE_APP_ROOT_API) changeURL(process.env.VUE_APP_ROOT_API)
        }

        const persistentStore = usePersistentStore()

        if (persistentStore.apiUrl?.includes('api.octopus.erkoware.cloud') && process.env?.VUE_APP_ROOT_API) {
            changeURL(process.env?.VUE_APP_ROOT_API)
        }

        setClientInformationHeaders()

        api.interceptors.request.use(async function (config) {
            if (!config.headers) config.headers = {} as AxiosHeaders
            const { getToken } = useAuth()
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            config.headers["Authorization"] = "Bearer " + await getToken();
            return config;
        });

        api.interceptors.response.use(function (response) {
            // Any status code that lie within the range of 2xx cause this function to trigger
            if (!useUserStore().sessionInfo.sessionToken) response.data = undefined

            return response;
        }, async function (error: { response?: { status: number; }; }) {
            if (error.response?.status === 401) {
                if (!route.value.meta?.noAuth) {

                    //! TODO: Implement failed authenticate logic
                    // localStorage.removeItem(TOKEN_KEY)
                    // Preferences.remove({key:TOKEN_KEY})
                    // localStorage.removeItem(ADMIN_KEY)
                    await useUserStore().resetStores()
                    await router.replace({ name: ROUTE_NAME.LOGIN })
                    setTimeout(() => {
                        testCount.value++
                    })
                }
                if (route.value.meta?.admin) router.replace({ name: 'LoginAdmin' })
                else router.replace({ name: ROUTE_NAME.LOGIN })
            }

            return Promise.reject(error);
        });

        initDone.value = true
    }

    return {
        api,
        testCount,
        changeURL,
        initApi
    }
}
